import { Component, DestroyRef, inject, output, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { InputLabel } from '../../../../parts/input-label/input-label.component';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { ChartOfAccountTypeaheadComponent } from '../../../../parts/chart-of-account-typeahead/chart-of-account-typeahead.component';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { ChartOfAccountCategory } from '../../../../core/enums/data.enums';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { PaymentService } from '../../../../core/services/payment/payment.service';
import { PaymentHistoryModel } from '../../../../core/models/data/payment-history.model';
import { PaymentIntentModel, RefundPaymentRequest } from '../../../../core/models/data/payment.model';
import { BalancesMessagingConstants, GenericMessagingConstants } from '../../../../core/constants/messaging.constants';
import { BaseFormComponent } from '../../../../core/base-classes/base-form-component';
import { EscCloseDirective } from '../../../../core/directives/esc-close.directive';
import { CanComponentDeactivate } from '../../../../core/routing/can-deactivate.guard';
import { DirtyFormRegistryService } from '../../../../core/services/dirty-form-registry.service';

@Component({
	selector: 'ms-refund-payment-modal',
	imports: [
		CommonModule,
		ReactiveFormsModule,
		FontAwesomeModule,
		InputLabel,
		ConfirmationModalComponent,
		InfoModalComponent,
		ChartOfAccountTypeaheadComponent,
		EscCloseDirective
	],
	templateUrl: './refund-payment-modal.component.html'
})
export class RefundPaymentModalComponent extends BaseFormComponent implements CanComponentDeactivate {

	//#region Properties & Signals

	public refundCompleted = output<PaymentIntentModel>();

	public payment = signal<PaymentHistoryModel | undefined>(undefined);
	public organizationId = signal<number | undefined>(undefined);
	public ChartOfAccountCategory = ChartOfAccountCategory;

	public confirmationMessage = BalancesMessagingConstants.REFUND_PAYMENT_CONFIRMATION();

	public refundForm: FormGroup;

	@ViewChild('refundPaymentInfoRef') infoRef!: InfoModalComponent;
	@ViewChild('refundPaymentEscConfirmationRef') escConfirmationRef!: ConfirmationModalComponent;

	//#endregion Properties & Signals

	//#region Constructor

	private fb = inject(FormBuilder);
	private paymentService = inject(PaymentService);
	private bootstrapService = inject(BootstrapService);
	private dirtyFormRegistry = inject(DirtyFormRegistryService);
	private destroyRef = inject(DestroyRef);

	constructor() {
		super();

		this.destroyRef.onDestroy(() => this.dirtyFormRegistry.unregister(this));

		this.refundForm = this.fb.group({
			payableChartOfAccount: [null, [Validators.required]],
			note: ['', [Validators.maxLength(255)]]
		});
	}

	//#endregion Constructor

	//#region Public Methods

	public async open(payment: PaymentHistoryModel, organizationId: number | undefined): Promise<void> {
		this.payment.set(payment);
		this.organizationId.set(organizationId);
		this.resetForm();
		this.dirtyFormRegistry.register(this);
		this.bootstrapService.manageModal('#refundPaymentModal', ModalActions.Show);
	}

	//#endregion Public Methods

	//#region Form Submission

	public async onSubmit(): Promise<void> {
		if (this.refundForm.invalid) {
			this.refundForm.markAllAsTouched();
			return;
		}

		const payment = this.payment();
		const paymentId = payment?.paymentId;
		if (paymentId === undefined) return;

		this.isSubmitting = true;

		try {
			const request = this.buildRequest();
			const result = await this.paymentService.refundPayment(paymentId, request);

			this.bootstrapService.manageModal('#refundPaymentModal', ModalActions.Hide);

			if (result.success && result.data) {
				this.submitSucceeded = true;
				this.infoRef.modalType.set(ModalTypes.Success);
				this.infoRef.message.set(BalancesMessagingConstants.REFUND_PAYMENT_SUCCESS());
				this.bootstrapService.manageModal('#refundPaymentInfoModal', ModalActions.Show);
				this.refundCompleted.emit(result.data);
			} else {
				this.infoRef.modalType.set(ModalTypes.Failure);
				this.infoRef.message.set(result.message || BalancesMessagingConstants.REFUND_PAYMENT_FAILURE());
				this.bootstrapService.manageModal('#refundPaymentInfoModal', ModalActions.Show);
			}
		} catch {
			this.bootstrapService.manageModal('#refundPaymentModal', ModalActions.Hide);
			this.infoRef.modalType.set(ModalTypes.Failure);
			this.infoRef.message.set(BalancesMessagingConstants.REFUND_PAYMENT_FAILURE());
			this.bootstrapService.manageModal('#refundPaymentInfoModal', ModalActions.Show);
		} finally {
			this.isSubmitting = false;
		}
	}

	//#endregion Form Submission

	//#region Modal Management

	public onCancel(): void {
		if (this.refundForm.dirty) {
			this.escConfirmationRef.modalType.set(ModalTypes.Question);
			this.escConfirmationRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
			this.bootstrapService.manageModal('#refundPaymentModal', ModalActions.Hide);
			this.bootstrapService.manageModal('#refundPaymentEscConfirmationModal', ModalActions.Show);
		} else {
			this.bootstrapService.manageModal('#refundPaymentModal', ModalActions.Hide);
			this.dirtyFormRegistry.unregister(this);
		}
	}

	public infoModalConfirmed(): void {
		this.bootstrapService.manageModal('#refundPaymentInfoModal', ModalActions.Hide);
		this.dirtyFormRegistry.unregister(this);
	}

	public canDeactivate(): boolean | Promise<boolean> {
		return this.awaitLeaveConfirmation(this.refundForm.dirty && !this.submitSucceeded, () => {
			this.escConfirmationRef.modalType.set(ModalTypes.Question);
			this.escConfirmationRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
			this.bootstrapService.manageModal('#refundPaymentModal', ModalActions.Hide);
			this.bootstrapService.manageModal('#refundPaymentEscConfirmationModal', ModalActions.Show);
		});
	}

	public onEscConfirmed(): void {
		this.bootstrapService.manageModal('#refundPaymentEscConfirmationModal', ModalActions.Hide);
		this.dirtyFormRegistry.unregister(this);
		this.resolvePendingLeave(true);
	}

	public onEscCancelled(): void {
		this.bootstrapService.manageModal('#refundPaymentEscConfirmationModal', ModalActions.Hide);
		this.bootstrapService.manageModal('#refundPaymentModal', ModalActions.Show);
		this.resolvePendingLeave(false);
	}

	//#endregion Modal Management

	//#region Private Helpers

	private buildRequest(): RefundPaymentRequest {
		const formValue = this.refundForm.value;
		return {
			payableChartOfAccount: formValue.payableChartOfAccount,
			note: formValue.note?.trim() || undefined
		};
	}

	private resetForm(): void {
		this.submitSucceeded = false;
		this.refundForm.reset({
			payableChartOfAccount: null,
			note: ''
		});
	}

	//#endregion Private Helpers
}
